import { authRolesSelector, userSelector } from '@/pages'
import { Header, Icon } from '@popety-io/ui'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { logout, updateLocalUser } from '@/pages/Login/redux'
import { updateUserLanguage } from '@/services/language'
// import { toggleThemeVariant } from '@/store/slices/appSlice'
// import { useAppSelector } from '@/store/hooks'
import {
  UserDropdown,
  UserDropdownItem,
  UserDropdownDivider,
  LanguageSection,
  LanguageLabel,
  LanguageFlags,
  FlagButton,
} from './Header.styles'
import './Header.css'

const AppHeader = () => {
  const user = useSelector(userSelector)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const roles = useSelector(authRolesSelector)
  const isLoggedIn = !!roles.length
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // const themeVariant = useAppSelector((state) => state.app.themeVariant)

  const isExplorePage =
    location.pathname === '/' ||
    location.pathname === '/developer-map' ||
    location.pathname === '/sale-listings' ||
    location.pathname === '/rental-listings' ||
    location.pathname === '/building-permits' ||
    location.pathname === '/transactions'

  const isAnalysisPage =
    location.pathname === '/land-analysis' ||
    location.pathname === '/land-groupings' ||
    location.pathname === '/building-analysis'

  const isEstimatePage = location.pathname === '/estimate'

  const isPipelinePage = location.pathname === '/pipeline'

  const isReportsPage = location.pathname === '/reports'

  // Check if pipeline is active for the user's company
  const isPipelineActive = user?.company?.policy?.isPipelineActive || false

  // Get active submenu info
  const getActiveSubmenuInfo = () => {
    type PathKey =
      | '/'
      | '/developer-map'
      | '/sale-listings'
      | '/rental-listings'
      | '/building-permits'
      | '/transactions'
      | '/land-analysis'
      | '/land-groupings'
      | '/building-analysis'

    const pathMap: Record<
      PathKey,
      { parent: string; label: string; icon: string }
    > = {
      '/': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.developerMap'),
        icon: 'land-plot',
      },
      '/developer-map': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.developerMap'),
        icon: 'land-plot',
      },
      '/sale-listings': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.saleListings'),
        icon: 'real-estate-listing',
      },
      '/rental-listings': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.rentalListings'),
        icon: 'real-estate-listing',
      },
      '/building-permits': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.buildingPermits'),
        icon: 'building-permit',
      },
      '/transactions': {
        parent: t('navigation.explore').toUpperCase(),
        label: t('navigation.transactions'),
        icon: 'real-estate-transaction',
      },
      '/land-analysis': {
        parent: t('navigation.analysis').toUpperCase(),
        label: t('navigation.landAnalysis'),
        icon: 'land-plot',
      },
      '/land-groupings': {
        parent: t('navigation.analysis').toUpperCase(),
        label: t('navigation.landGroupings'),
        icon: 'layers',
      },
      '/building-analysis': {
        parent: t('navigation.analysis').toUpperCase(),
        label: t('navigation.buildingAnalysis'),
        icon: 'home',
      },
    }

    if ((location.pathname as PathKey) in pathMap) {
      return pathMap[location.pathname as PathKey]
    }
    return null
  }

  const activeSubmenuInfo = getActiveSubmenuInfo()

  const navItems = isLoggedIn
    ? [
        {
          label: (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
              }}
            >
              <span>{t('navigation.explore').toUpperCase()}</span>
              {isExplorePage && activeSubmenuInfo && (
                <span
                  style={{
                    fontSize: '10px',
                    color: '#6b6b6b',
                    fontWeight: 300,
                    lineHeight: '4px',
                  }}
                >
                  {activeSubmenuInfo.label}
                </span>
              )}
            </div>
          ),
          active: isExplorePage,
          items: [
            {
              label: t('navigation.developerMap'),
              active:
                location.pathname === '/developer-map' ||
                location.pathname === '/',
              onClick: () => navigate('/developer-map'),
              icon: <Icon name="land-plot" size="small" />,
            },
            {
              label: t('navigation.saleListings'),
              active: location.pathname === '/sale-listings',
              onClick: () => navigate('/sale-listings'),
              icon: <Icon name="real-estate-listing" size="small" />,
            },
            {
              label: t('navigation.rentalListings'),
              active: location.pathname === '/rental-listings',
              onClick: () => navigate('/rental-listings'),
              icon: <Icon name="real-estate-listing" size="small" />,
            },
            {
              label: t('navigation.buildingPermits'),
              active: location.pathname === '/building-permits',
              onClick: () => navigate('/building-permits'),
              icon: <Icon name="building-permit" size="small" />,
            },
            {
              label: t('navigation.transactions'),
              active: location.pathname === '/transactions',
              onClick: () => navigate('/transactions'),
              icon: <Icon name="real-estate-transaction" size="small" />,
            },
          ],
        },
        {
          label: (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
              }}
            >
              <span>{t('navigation.analysis').toUpperCase()}</span>
              {isAnalysisPage && activeSubmenuInfo && (
                <span
                  style={{
                    fontSize: '10px',
                    color: '#6b6b6b',
                    fontWeight: 300,
                    lineHeight: '4px',
                  }}
                >
                  {activeSubmenuInfo.label}
                </span>
              )}
            </div>
          ),
          active: isAnalysisPage,
          items: [
            {
              label: t('navigation.landAnalysis'),
              active: location.pathname === '/land-analysis',
              onClick: () => navigate('/land-analysis'),
              icon: <Icon name="land-plot" size="small" />,
            },
            {
              label: t('navigation.landGroupings'),
              active: location.pathname === '/land-groupings',
              onClick: () => navigate('/land-groupings'),
              icon: <Icon name="layers" size="small" />,
            },
            {
              label: t('navigation.buildingAnalysis'),
              active: location.pathname === '/building-analysis',
              onClick: () => navigate('/building-analysis'),
              icon: <Icon name="home" size="small" />,
            },
          ],
        },
        {
          label: t('navigation.estimate').toUpperCase(),
          active: isEstimatePage,
          onClick: () => navigate('/estimate'),
        },
      ]
    : []

  // Add Pipeline menu item if it's active for the user's company
  if (isLoggedIn && isPipelineActive) {
    navItems.push({
      label: t('navigation.pipeline').toUpperCase(),
      active: isPipelinePage,
      onClick: () => navigate('/pipeline'),
    })
  }

  // Add Reports menu item
  if (isLoggedIn) {
    navItems.push({
      label: t('navigation.reports').toUpperCase(),
      active: isReportsPage,
      onClick: () => navigate('/reports'),
    })
  }

  // const handleThemeToggle = () => {
  //   dispatch(toggleThemeVariant())
  // }

  const handleLanguageChange = (lang: string) => {
    // Update i18next (also saves to localStorage['i18nextLng'])
    i18n.changeLanguage(lang)

    // Sync to database if logged in
    if (isLoggedIn) {
      updateUserLanguage(lang)
      dispatch(updateLocalUser({ lang }))
    }
  }

  // Language flags configuration
  const languageFlags = [
    { code: 'en', flag: '🇬🇧' },
    { code: 'fr', flag: '🇫🇷' },
    { code: 'de', flag: '🇩🇪' },
    { code: 'it', flag: '🇮🇹' },
  ]

  const logo = (
    <div
      onClick={() => navigate('/developer-map')}
      style={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <img
        height={32}
        width="auto"
        src="https://vp5o2gbqfxt.preview.infomaniak.website/wp-content/uploads/2025/01/popety-io-logo.png"
        alt="popety.io"
      />
    </div>
  )

  // Handle dropdown outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  const handleUserClick = () => {
    setDropdownOpen(!dropdownOpen)
  }

  const handleLogout = () => {
    setDropdownOpen(false)
    dispatch(logout())
    // Force a page reload to ensure clean logout and navigation
    window.location.href = '/login'
  }

  const handleDropdownItemClick = (path: string) => {
    navigate(path)
    setDropdownOpen(false)
  }

  return (
    <>
      <Header
        navItems={navItems}
        logo={logo}
        user={
          isLoggedIn && user
            ? {
                name: `${user?.first_name || ''} ${user?.last_name || ''}`,
                initials: `${user?.first_name?.charAt(0) || ''}${user?.last_name?.charAt(0) || ''}`,
                avatar: user?.photo,
              }
            : undefined
        }
        onUserClick={handleUserClick}
        sticky={false}
        className="app-header"
        showSearch={false}
      />
      {dropdownOpen && (
        <UserDropdown ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
          <UserDropdownItem onClick={() => handleDropdownItemClick('/alerts')}>
            <Icon name="bell" size="small" />
            <span>{t('common.alerts')}</span>
          </UserDropdownItem>
          <UserDropdownItem
            onClick={() => handleDropdownItemClick('/dashboard')}
          >
            <Icon name="chart" size="small" />
            <span>{t('common.dashboard')}</span>
          </UserDropdownItem>
          <UserDropdownDivider />
          <UserDropdownItem
            onClick={() => handleDropdownItemClick('/settings')}
          >
            <Icon name="settings" size="small" />
            <span>{t('common.settings')}</span>
          </UserDropdownItem>
          <UserDropdownItem onClick={() => handleDropdownItemClick('/modules')}>
            <Icon name="grid" size="small" />
            <span>{t('common.viewModules')}</span>
          </UserDropdownItem>
          <UserDropdownDivider />
          <LanguageSection>
            <LanguageLabel>
              <Icon name="globe" size="small" />
              <span>{t('common.language')}</span>
            </LanguageLabel>
            <LanguageFlags>
              {languageFlags.map(({ code, flag }) => (
                <FlagButton
                  key={code}
                  $active={i18n.language === code}
                  onClick={() => handleLanguageChange(code)}
                >
                  {flag}
                </FlagButton>
              ))}
            </LanguageFlags>
          </LanguageSection>
          <UserDropdownDivider />
          <UserDropdownItem onClick={handleLogout}>
            <Icon name="log-out" size="small" />
            <span>{t('common.logout')}</span>
          </UserDropdownItem>
        </UserDropdown>
      )}
    </>
  )
}

export default AppHeader
